// src/components/Carrito/Carrito.js
import React, { useEffect, useMemo, useState } from 'react';
import './Carrito.css';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase/firebase';
import { createPortal } from 'react-dom';

const nf = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

const STORE = {
  whatsapp: '+54 9 11 6003 5550',
  productionDays: 9,
  mapsUrl: 'https://www.google.com/maps?q=Juncal+2064,+CABA',
  pickupLabel: 'Retiro en tienda (sin costo)',
};

const buildWALink = (number, text) =>
  `https://wa.me/${number.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;

// ===== Drawer a <body>
function DrawerPortal({ open, onClose, children }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? 'hidden' : prev || '';
    return () => { document.body.style.overflow = prev || ''; };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="jpvDrawer jpvDrawer--open" aria-hidden={!open}>
      <div className="jpvDrawer__backdrop" onClick={onClose} />
      <aside className="jpvDrawer__panel" role="dialog" aria-modal="true">
        {children}
      </aside>
    </div>,
    document.body
  );
}

// --- Zonas de envío
function normalize(str = '') { return String(str).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, ''); }
function getShippingZone(provinciaInput = '', localidadInput = '') {
  const provRaw = (provinciaInput || '').trim();
  if (!provRaw) return null;
  const prov = normalize(provinciaInput);
  const loc = normalize(localidadInput || '');
  const isCABA =
    prov.includes('caba') || prov.includes('ciudad autonoma') || prov.includes('ciudad autónoma') ||
    loc.includes('caba') || loc.includes('capital federal') || loc === 'capital';
  if (isCABA) return { key: 'CABA', cost: 15000 };
  const isPBA = prov.includes('buenos aires') || prov === 'pba';
  if (isPBA) return { key: 'AMBA', cost: 20000 };
  return { key: 'PROV', cost: 25000 };
}

const Carrito = () => {
  // ===== sesión y carrito
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')) || null; } catch { return null; }
  });

  const [cart, setCart] = useState(() => {
    try {
      if (localStorage.getItem('user')) return JSON.parse(localStorage.getItem('carrito')) || [];
      return [];
    } catch { return []; }
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        const cached = { uid: fbUser.uid, email: fbUser.email, name: fbUser.displayName || '' };
        localStorage.setItem('user', JSON.stringify(cached));
        setUser(cached);
        const storedCart = JSON.parse(localStorage.getItem('carrito')) || [];
        setCart(storedCart);
      } else {
        localStorage.removeItem('user');
        setUser(null);
        setCart([]);
      }
    });
    return () => unsubscribe();
  }, []);

  const saveCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem('carrito', JSON.stringify(newCart));
    window.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: newCart.length } }));
  };

  const handleRemove = (id) => saveCart(cart.filter(item => item.id !== id));
  const updateQuantity = (id, delta) => {
    const updated = cart.map(item => {
      if (item.id === id) {
        const qty = Math.max(1, Number(item.quantity || 1) + delta);
        return { ...item, quantity: qty };
      }
      return item;
    });
    saveCart(updated);
    // si estoy en modo "single", reflejo el cambio del item seleccionado
    if (selectedItem && selectedItem.id === id) {
      setSelectedItem(si => ({ ...si, quantity: Math.max(1, Number((si?.quantity ?? 1)) + delta) }));
    }
  };

  // ===== Drawer de checkout
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState('all'); // 'all' | 'single'
  const [selectedItem, setSelectedItem] = useState(null);  // en modo 'single'

  // pago
  const [payMethod, setPayMethod] = useState('mp');      // 'mp' | 'card' | 'cash'
  const [prefError, setPrefError] = useState('');
  const [creatingPref, setCreatingPref] = useState(false);

  // Cupón
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMsg, setCouponMsg] = useState('');

  // Entrega
  const [delivery, setDelivery] = useState('retiro');     // 'retiro' | 'envio'
  const [cp, setCp] = useState('');
  const [address, setAddress] = useState({ calle: '', numero: '', piso: '', dpto: '', localidad: '', provincia: '' });
  const [phone, setPhone] = useState('');

  const zoneInfo = useMemo(() => getShippingZone(address.provincia, address.localidad), [address.provincia, address.localidad]);
  const shippingCost = useMemo(
    () => (delivery === 'envio' && zoneInfo ? zoneInfo.cost : 0),
    [delivery, zoneInfo]
  );

  // padding inferior dinámico para que el sticky button no tape contenido (incluye safe-area iOS)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)').matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const h = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', h); else mq.addListener(h);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', h); else mq.removeListener(h); };
  }, []);
  // 1) bodyPadBottom más chico
  const bodyPadBottom = isMobile
    ? 'calc(28px + env(safe-area-inset-bottom))'  // antes 92px
    : 'calc(10px + env(safe-area-inset-bottom))'; // antes 56px


  // abrir drawer
  const openCheckoutOne = (item) => {
    setSelectedItem(item);
    setCheckoutMode('single');
    setPayMethod('mp');
    setPrefError('');
    setAppliedCoupon(null);
    setCouponInput('');
    setDelivery('retiro');
    setAddress({ calle: '', numero: '', piso: '', dpto: '', localidad: '', provincia: '' });
    setCp('');
    setPhone('');
    setShowCheckout(true);
  };
  const openCheckoutAll = () => {
    setSelectedItem(null);
    setCheckoutMode('all');
    setPayMethod('mp');
    setPrefError('');
    setAppliedCoupon(null);
    setCouponInput('');
    setDelivery('retiro');
    setAddress({ calle: '', numero: '', piso: '', dpto: '', localidad: '', provincia: '' });
    setCp('');
    setPhone('');
    setShowCheckout(true);
  };

  // totales
  const singleSubtotal = useMemo(() => {
    if (!selectedItem) return 0;
    return Math.round(Number(selectedItem.price || 0)) * Math.max(1, Number(selectedItem.quantity || 1));
  }, [selectedItem]);

  const cartSubtotal = useMemo(
    () => cart.reduce((s, it) => s + (Math.round(Number(it.price || 0)) * Math.max(1, Number(it.quantity || 1))), 0),
    [cart]
  );

  const baseSubtotal = checkoutMode === 'single' ? singleSubtotal : cartSubtotal;
  const couponDiscount = appliedCoupon ? Math.round(baseSubtotal * (appliedCoupon.percent / 100)) : 0;
  const total = Math.max(0, baseSubtotal - couponDiscount) + shippingCost;

  // ===== WhatsApp “enmarcado”
  const waMsg = useMemo(() => {
    const items = (checkoutMode === 'single' && selectedItem ? [selectedItem] : cart);
    const lines = items.map(it => `• ${it.name} x${Math.max(1, Number(it.quantity || 1))} — ${nf.format(Math.round(Number(it.price || 0)))}`);
    const entrega = delivery === 'retiro' ? STORE.pickupLabel : `Envío a domicilio ${zoneInfo ? `(${nf.format(zoneInfo.cost)})` : ''}`;
    return [
      'Hola Juani 👋 Consulto por enmarcado.',
      '',
      'Carrito:',
      ...lines,
      '',
      `Entrega: ${entrega}`,
      `Producción estimada: ${STORE.productionDays} días`,
    ].join('\n');
  }, [checkoutMode, selectedItem, cart, delivery, zoneInfo]);

  const waLinkEnmarcado = buildWALink(STORE.whatsapp, waMsg);

  // cupón
  const applyCoupon = async () => {
    const code = (couponInput || '').trim();
    setCouponMsg('');
    if (!code) return;
    try {
      const resp = await fetch('/api/discount/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      const data = await resp.json();
      if (!resp.ok || !data?.valid) throw new Error(data?.message || 'Cupón inválido');
      setAppliedCoupon({ code: data.code, percent: Number(data.percent || 0) });
      setCouponMsg(`Cupón aplicado: ${data.code} (-${data.percent}%)`);
    } catch (e) {
      setAppliedCoupon(null);
      setCouponMsg(e?.message || 'Cupón inválido');
    }
  };
  const removeCoupon = () => { setAppliedCoupon(null); setCouponInput(''); setCouponMsg('Cupón quitado'); };

  // helpers de pago
  async function pagarTodo() {
    const resp = await fetch('/api/mp/create-preference', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: `Carrito (${cart.length} items)`,
        quantity: 1,
        unit_price: Math.round(total), // total con cupón + envío
        buyerEmail: user?.email || localStorage.getItem('authEmail') || '',
        payMethod,
        delivery,
        shippingCost: Math.round(Number(shippingCost || 0)),
        address: { cp, telefono: phone, ...address },
        metadata: {
          kind: 'cart',
          coupon: appliedCoupon || null,
          items: cart
        }
      })
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data?.message || 'Fallo al crear preference');
    const urlToPay = data.init_point || data.sandbox_init_point;
    if (!urlToPay) throw new Error('Respuesta sin init_point');
    window.location.assign(urlToPay);
  }

  async function pagarUno() {
    const it = selectedItem;
    if (!it) return;

    const qty = Math.max(1, Number(it.quantity || 1));
    const unit = Math.round(Number(it.price || 0));
    const lineSubtotal = unit * qty;
    const lineTotal = Math.max(0, lineSubtotal - (appliedCoupon ? Math.round(lineSubtotal * (appliedCoupon.percent / 100)) : 0));

    const resp = await fetch('/api/mp/create-preference', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: `${it.name} x${qty}`,
        quantity: 1,
        unit_price: Math.round(lineTotal + shippingCost),
        buyerEmail: user?.email || localStorage.getItem('authEmail') || '',
        payMethod,
        delivery,
        shippingCost: Math.round(Number(shippingCost || 0)),
        address: { cp, telefono: phone, ...address },
        metadata: {
          kind: 'cart-item',
          id: it.id,
          unit,
          qty,
          coupon: appliedCoupon || null
        }
      })
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data?.message || 'Fallo al crear preference');
    const urlToPay = data.init_point || data.sandbox_init_point;
    if (!urlToPay) throw new Error('Respuesta sin init_point');
    window.location.assign(urlToPay);
  }

  const finalizarCompra = async () => {
    try {
      setCreatingPref(true);
      setPrefError('');

      if (delivery === 'envio') {
        const ok = cp && address.calle && address.numero && address.localidad && address.provincia && zoneInfo && (phone || '').trim().length >= 8;
        if (!ok) throw new Error('Completá los datos de envío');
      }
      if (baseSubtotal <= 0) throw new Error('transaction_amount must be positive');

      if (checkoutMode === 'all') await pagarTodo();
      else await pagarUno();
    } catch (e) {
      console.error(e);
      setPrefError(e?.message || 'No se pudo iniciar el pago');
    } finally {
      setCreatingPref(false);
    }
  };

  const totalCart = cart.reduce((sum, i) => sum + Math.round(Number(i.price || 0)) * Math.max(1, Number(i.quantity || 1)), 0);

  return (
    <div className="cart-container">
      <h1 className="cart-title">Carrito de Compras</h1>

      {!user ? (
        <p className="cart-login-message">
          Para ver tu carrito primero tenés que{' '}
          <a href="/login" className="cart-login-link">iniciar sesión</a>.
        </p>
      ) : cart.length === 0 ? (
        <p className="cart-empty">No hay productos en el carrito.</p>
      ) : (
        <>
          <p className="cart-subtitle">Total de elementos en carrito: {cart.length}</p>

          <div className="cart-items">
            {cart.map(item => {
              const lineTotal = Math.round(Number(item.price || 0)) * Math.max(1, Number(item.quantity || 1));
              return (
                <div className="cart-item" key={item.id}>
                  <button className="remove-btn" onClick={() => handleRemove(item.id)} aria-label="Quitar producto" title="Quitar producto">
                    &times;
                  </button>

                  <img src={item.image} alt={item.name} className="item-image" draggable="false" onDragStart={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()} />

                  <div className="item-info">
                    <p className="item-name">{item.name}</p>
                    <p className="item-price">{nf.format(lineTotal)}</p>
                  </div>

                  <div className="quantity-controls">
                    <button onClick={() => updateQuantity(item.id, -1)} aria-label="Disminuir cantidad" disabled={Number(item.quantity || 1) <= 1}>-</button>
                    <span>{Math.max(1, Number(item.quantity || 1))}</span>
                    <button onClick={() => updateQuantity(item.id, +1)} aria-label="Aumentar cantidad">+</button>
                  </div>

                  <button className="buy-btn" onClick={() => openCheckoutOne(item)}>
                    Comprar este producto
                  </button>
                </div>
              );
            })}
          </div>

          <div className="cart-footer">
            <div className="footer-left">
              <p className="cart-total">
                Subtotal: <strong>{nf.format(totalCart)}</strong>
              </p>
              <p className="shipping-note">El envío se calcula en el checkout</p>
            </div>
            <button className="buy-all-btn" onClick={openCheckoutAll}>Comprar todo</button>
          </div>
        </>
      )}

      {/* ===== Drawer */}
      <DrawerPortal open={showCheckout} onClose={() => setShowCheckout(false)}>
        <div className="jpvDrawer__header">
          <strong>Compra</strong>
          <button className="jpvDrawer__close" onClick={() => setShowCheckout(false)} aria-label="Cerrar">×</button>
        </div>

        {/* body con padding inferior dinámico */}
        <div className="jpvDrawer__body" style={{ paddingBottom: bodyPadBottom }}>
          {/* Mini-resúmenes */}
          {checkoutMode === 'single' && selectedItem && (
            <div className="mini-item">
              <img src={selectedItem.image} alt="thumb" />
              <div className="mini-info">
                <div className="mini-title">{selectedItem.name}</div>
                <div className="mini-sub">Cantidad: {Math.max(1, Number(selectedItem.quantity || 1))}</div>
                <div className="mini-price">{nf.format(Math.round(Number(selectedItem.price || 0)))}</div>
              </div>
              <div className="qty">
                <button
                  onClick={() => updateQuantity(selectedItem.id, -1)}
                  aria-label="menos"
                  disabled={Number(selectedItem.quantity || 1) <= 1}
                >−</button>
                <span>{Math.max(1, Number(cart.find(i => i.id === selectedItem.id)?.quantity ?? selectedItem.quantity ?? 1))}</span>
                <button
                  onClick={() => updateQuantity(selectedItem.id, +1)}
                  aria-label="más"
                >+</button>
              </div>
            </div>
          )}

          {checkoutMode === 'all' && cart.map((it) => (
            <div className="mini-item" key={`chk-${it.id}`}>
              <img src={it.image} alt="thumb" />
              <div className="mini-info">
                <div className="mini-title">{it.name}</div>
                <div className="mini-sub">Cantidad: {Math.max(1, Number(it.quantity || 1))}</div>
                <div className="mini-price">{nf.format(Math.round(Number(it.price || 0)))}</div>
              </div>
              <div className="qty">
                <button onClick={() => updateQuantity(it.id, -1)} aria-label="menos" disabled={Number(it.quantity || 1) <= 1}>−</button>
                <span>{Math.max(1, Number(it.quantity || 1))}</span>
                <button onClick={() => updateQuantity(it.id, +1)} aria-label="más">+</button>
              </div>
            </div>
          ))}

          {/* Entrega */}
          <div className="section">
            <div className="section-title">Entrega</div>
            <div className="toggle-group" role="tablist" aria-label="Elegir entrega">
              <button type="button" className={`size-btn ${delivery === 'retiro' ? 'active' : ''}`} role="tab" aria-selected={delivery === 'retiro'} onClick={() => setDelivery('retiro')}>Retiro en tienda (sin costo)</button>
              <button type="button" className={`size-btn ${delivery === 'envio' ? 'active' : ''}`} role="tab" aria-selected={delivery === 'envio'} onClick={() => setDelivery('envio')}>Envío a domicilio</button>
            </div>

            {delivery === 'retiro' && (
              <div className="card card-soft">
                <div className="card-title">Taller Juani Pavlovsky</div>
                <div className="card-sub">Juncal 2064, CABA</div>
                <div className="card-sub">Horarios: Lun a Vie 9–17 hs</div>
                <div className="card-note">⏱ Se puede retirar {STORE.productionDays} días después de la compra.</div>
                <a className="link-arrow" href={STORE.mapsUrl} target="_blank" rel="noreferrer">Ver ubicación en Google Maps</a>
              </div>
            )}

            {delivery === 'envio' && (
              <div className="card card-soft">
                <div className="shipping-form">
                  <div className="row inline">
                    <label>Código postal</label>
                    <input value={cp} onChange={e => setCp(e.target.value)} placeholder="Ej: 1425" />
                  </div>
                  <div className="row two">
                    <div><label>Calle</label><input value={address.calle} onChange={e => setAddress(a => ({ ...a, calle: e.target.value }))} /></div>
                    <div><label>Número</label><input value={address.numero} onChange={e => setAddress(a => ({ ...a, numero: e.target.value }))} /></div>
                  </div>
                  <div className="row two">
                    <div><label>Piso</label><input value={address.piso} onChange={e => setAddress(a => ({ ...a, piso: e.target.value }))} /></div>
                    <div><label>Dpto</label><input value={address.dpto} onChange={e => setAddress(a => ({ ...a, dpto: e.target.value }))} /></div>
                  </div>
                  <div className="row two">
                    <div><label>Localidad</label><input value={address.localidad} onChange={e => setAddress(a => ({ ...a, localidad: e.target.value }))} /></div>
                    <div><label>Provincia</label><input value={address.provincia} onChange={e => setAddress(a => ({ ...a, provincia: e.target.value }))} /></div>
                  </div>
                  <div className="row inline">
                    <label>Teléfono</label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Ej: 11 2345-6789" />
                  </div>
                </div>

                <div className="sep" />
                <div className="row-between"><span>Costo de envío</span><strong>{zoneInfo ? nf.format(zoneInfo.cost) : '—'}</strong></div>
                <small className="muted">{zoneInfo ? 'El envío se suma a los 9 días de producción.' : 'Ingresá provincia y localidad para calcular el costo.'}</small>
              </div>
            )}
          </div>

          {/* Pago */}
          <div className="section">
            <div className="section-title">Pago</div>

            <div className="toggle-group" role="tablist" aria-label="Elegir forma de pago">
              <button type="button" className={`size-btn ${payMethod === 'mp' ? 'active' : ''}`} role="tab" aria-selected={payMethod === 'mp'} onClick={() => setPayMethod('mp')}>Mercado Pago (billetera)</button>
              <button type="button" className={`size-btn ${payMethod === 'card' ? 'active' : ''}`} role="tab" aria-selected={payMethod === 'card'} onClick={() => setPayMethod('card')}>Tarjeta</button>
              <button type="button" className={`size-btn ${payMethod === 'cash' ? 'active' : ''}`} role="tab" aria-selected={payMethod === 'cash'} onClick={() => setPayMethod('cash')}>Efectivo</button>
            </div>

            {/* Cupón */}
            <div className="coupon-box">
              <label htmlFor="coupon">Código de descuento</label>
              <div className="coupon-row">
                <input id="coupon" value={couponInput} onChange={(e) => setCouponInput(e.target.value)} className='input' />
                <button type="button" onClick={applyCoupon}>Aplicar</button>
                {appliedCoupon && <button type="button" className="link-btn" onClick={removeCoupon}>Quitar cupón</button>}
              </div>
              {couponMsg && <small className="muted">{couponMsg}</small>}
              {appliedCoupon && <small className="muted">Aplicado: {appliedCoupon.code} (−{appliedCoupon.percent}%)</small>}
            </div>
          </div>

          {/* Totales */}
          <div className="totals">
            <div><span>Subtotal</span><span>{nf.format(baseSubtotal)}</span></div>
            {appliedCoupon && (<div><span>Descuento cupón ({appliedCoupon.percent}%)</span><span>-{nf.format(couponDiscount)}</span></div>)}
            <div><span>Envío</span><span>{delivery === 'envio' ? (zoneInfo ? nf.format(shippingCost) : '—') : 'Sin costo'}</span></div>
            <div className="grand"><span>Total</span><span>{nf.format(total)}</span></div>
          </div>

          {prefError && <div className="error-box">{prefError}</div>}

          {/* Mensaje WhatsApp */}
          <div className="wa-box">
            <a className="btn-wa" href={waLinkEnmarcado} target="_blank" rel="noreferrer">Consultar enmarcado por WhatsApp</a><br />
            <span className="wa-hint">El enmarcado se cotiza y paga aparte.</span>
          </div>
        </div>

        {/* Footer sticky SOLO con el botón; fondo 100% transparente y compacto */}
        <div
          className="drawer-sticky"            // <-- NUEVO: clase para el sticky
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 5,
            padding: '4px 12px',               // antes 6px 12px
            paddingBottom: 'calc(4px + env(safe-area-inset-bottom))',
          }}
        >
          <button
            className="btn-primary"
            onClick={finalizarCompra}
            disabled={
              creatingPref ||
              baseSubtotal <= 0 ||
              (delivery === 'envio' && (!cp || !address.calle || !address.numero || !address.localidad || !address.provincia || !zoneInfo || phone.trim().length < 8))
            }
          >
            {creatingPref ? 'Creando pago…' : 'Finalizar compra'}
          </button>
        </div>

      </DrawerPortal>
    </div>
  );
};

export default Carrito;
